import { Box, Grid, Paper, Stack } from "@mui/material";
import ShowWithSkeleton from "src/utils/show-with-skeleton";
import executiveValueFormatter from "src/scenes/overview/overview-components/executive-value-formatter";
import React from "react";
import styled from "styled-components";
import ReactCardFlip from "react-card-flip";
import calculateDependencies from "src/scenes/overview/overview-components/calculate-dependencies";
import AccuracyLinear from "src/scenes/overview/overview-components/accuracy-linear";
import { docsPathConstants, getDocsPath } from "src/utils/docs-path-constants";
import { useOrg } from "src/components/providers/org-provider";
import ExternalLink from "../../../../components/external-link";

const CustomPaper = styled(Paper)`
    width: 100%;
    padding: 16px;
    border-radius: 14px 14px 0 0;
    min-height: 271px;
`;

const MetricHeaderText = styled.p`
    margin: 12px 0 12px 0;
    font-size: 15px;
    font-weight: 600;
`;

const MetricBreakdownText = styled.p`
    margin: 0;
    font-size: 12px;
    font-weight: 300;
`;

const InfoText = styled.p`
    margin: 0;
    font-size: 12px;
    font-weight: 200;
`;

const BreakdownContainer = styled(Box)`
    padding: 12px;
    border-radius: 12px;
    background: #393939;
    margin-top: 12px;
`;

const DevCost = ({ fetchAction, data, expandedView, isFlipped }) => {
    const { org } = useOrg();
    return (
        <ReactCardFlip isFlipped={isFlipped} containerStyle={{ width: "100%", height: "100%" }}>
            <CustomPaper>
                {!isFlipped && (
                    <>
                        <MetricHeaderText style={{ margin: 0, fontWeight: 200, opacity: 0.5 }}>
                            Personal Credit Cost
                        </MetricHeaderText>
                        <Grid container spacing={1}>
                            <Grid item md={6} mb={1} sm={12}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <MetricHeaderText>Long Working Hours</MetricHeaderText>
                                    <AccuracyLinear
                                        data={
                                            data &&
                                            data.longHours &&
                                            data.longHours.meta &&
                                            data.longHours.meta.accuracy
                                        }
                                    />
                                </Stack>
                                <ShowWithSkeleton
                                    fetchAction={fetchAction}
                                    height={50}
                                    width="100%"
                                    streamingProperty={
                                        fetchAction &&
                                        fetchAction.data &&
                                        fetchAction.data.longHours
                                    }
                                >
                                    {executiveValueFormatter(
                                        data && data.longHours && data.longHours.total.value,
                                        "time",
                                        false,
                                        "per user-work day",
                                        data &&
                                            data.longHours &&
                                            data.longHours.stats &&
                                            data.longHours.stats.slope
                                    )}
                                </ShowWithSkeleton>
                                {expandedView && (
                                    <BreakdownContainer>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>Total sum:</MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.longHours &&
                                                    fetchAction.data.longHours.breakdown &&
                                                    fetchAction.data.longHours.breakdown.total
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.longHours &&
                                                        data.longHours.breakdown.total,
                                                    "time",
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Per day (summed users):
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.longHours &&
                                                    fetchAction.data.longHours.breakdown &&
                                                    fetchAction.data.longHours.breakdown.avgPerDay
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.longHours &&
                                                        data.longHours.breakdown.avgPerDay,
                                                    "time",
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Per user (for entire period):
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.longHours &&
                                                    fetchAction.data.longHours.breakdown &&
                                                    fetchAction.data.longHours.breakdown.avgPerUser
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.longHours &&
                                                        data.longHours.breakdown.avgPerUser,
                                                    "time",
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                    </BreakdownContainer>
                                )}
                            </Grid>
                            <Grid item md={6} mb={1} sm={12}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <MetricHeaderText>Out of hours working</MetricHeaderText>
                                    <AccuracyLinear
                                        data={
                                            data &&
                                            data.outOfHours &&
                                            data.outOfHours.meta &&
                                            data.outOfHours.meta.accuracy
                                        }
                                    />
                                </Stack>
                                <ShowWithSkeleton
                                    fetchAction={fetchAction}
                                    height={50}
                                    width="100%"
                                    streamingProperty={
                                        fetchAction &&
                                        fetchAction.data &&
                                        fetchAction.data.outOfHours
                                    }
                                >
                                    {executiveValueFormatter(
                                        data && data.outOfHours && data.outOfHours.total.value,
                                        "time",
                                        false,
                                        "per user-work day",
                                        data &&
                                            data.outOfHours &&
                                            data.outOfHours.stats &&
                                            data.outOfHours.stats.slope
                                    )}
                                </ShowWithSkeleton>
                                {expandedView && (
                                    <BreakdownContainer>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>Total sum:</MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.outOfHours &&
                                                    fetchAction.data.outOfHours.breakdown &&
                                                    fetchAction.data.outOfHours.breakdown.total
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.outOfHours &&
                                                        data.outOfHours.breakdown.total,
                                                    "time",
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Per day (summed users):
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.outOfHours &&
                                                    fetchAction.data.outOfHours.breakdown &&
                                                    fetchAction.data.outOfHours.breakdown.avgPerDay
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.outOfHours &&
                                                        data.outOfHours.breakdown.avgPerDay,
                                                    "time",
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>
                                                Per user (for entire period):
                                            </MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.outOfHours &&
                                                    fetchAction.data.outOfHours.breakdown &&
                                                    fetchAction.data.outOfHours.breakdown.avgPerUser
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.outOfHours &&
                                                        data.outOfHours.breakdown.avgPerUser,
                                                    "time",
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                    </BreakdownContainer>
                                )}
                            </Grid>
                            <Grid item md={6} mb={1} sm={12}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <MetricHeaderText>Available Focus Time</MetricHeaderText>
                                    <AccuracyLinear
                                        data={
                                            data &&
                                            data.focus &&
                                            data.focus.meta &&
                                            data.focus.meta.accuracy
                                        }
                                    />
                                </Stack>
                                <ShowWithSkeleton
                                    fetchAction={fetchAction}
                                    height={50}
                                    width="100%"
                                    streamingProperty={
                                        fetchAction && fetchAction.data && fetchAction.data.focus
                                    }
                                >
                                    {executiveValueFormatter(
                                        data && data.focus && data.focus.total.value,
                                        "time",
                                        false,
                                        "per user-work day",
                                        data &&
                                            data.focus &&
                                            data.focus.stats &&
                                            data.focus.stats.slope
                                    )}
                                </ShowWithSkeleton>
                                {expandedView && (
                                    <BreakdownContainer>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>Total sum:</MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.focus &&
                                                    fetchAction.data.focus.breakdown &&
                                                    fetchAction.data.focus.breakdown.total
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.focus &&
                                                        data.focus.breakdown.total,
                                                    "time",
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <MetricBreakdownText>Per user:</MetricBreakdownText>
                                            <ShowWithSkeleton
                                                fetchAction={fetchAction}
                                                streamingProperty={
                                                    fetchAction &&
                                                    fetchAction.data &&
                                                    fetchAction.data.focus &&
                                                    fetchAction.data.focus.breakdown &&
                                                    fetchAction.data.focus.breakdown.perUser
                                                }
                                            >
                                                {executiveValueFormatter(
                                                    data &&
                                                        data.focus &&
                                                        data.focus.breakdown.perUser,
                                                    "time",
                                                    true
                                                )}
                                            </ShowWithSkeleton>
                                        </Stack>
                                    </BreakdownContainer>
                                )}
                            </Grid>
                        </Grid>
                    </>
                )}
            </CustomPaper>
            <CustomPaper>
                {isFlipped && (
                    <>
                        <MetricHeaderText
                            style={{ margin: "0 0 10px 0", fontWeight: 200, opacity: 0.5 }}
                        >
                            Personal Credit Cost
                        </MetricHeaderText>
                        <InfoText>
                            These are costs that might not impact current output delivery, but they
                            will eventually need to be &quot;paid&quot;. These costs can affect the
                            team&apos;s mental health or their ability to sustain this way of
                            working over the long term.
                        </InfoText>
                        <Grid container spacing={1}>
                            <Grid item md={6} mb={1} sm={12}>
                                <MetricHeaderText>
                                    <ExternalLink
                                        href={getDocsPath(
                                            org,
                                            docsPathConstants.personalCreditLongHoursDefinition
                                        )}
                                        underline="none"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Long Working Hours
                                    </ExternalLink>
                                </MetricHeaderText>
                                <InfoText>
                                    Person-working days spent working more hours than the normal
                                    working hours (as defined in their profile).
                                </InfoText>
                                <Stack
                                    direction="column"
                                    alignItems="left"
                                    spacing={1}
                                    mt={1}
                                    style={{ width: "100%" }}
                                >
                                    <InfoText>Data source impact:</InfoText>
                                    {calculateDependencies(
                                        data &&
                                            data.longHours &&
                                            data.longHours.meta.accuracy.dependencies
                                    )}
                                </Stack>
                            </Grid>
                            <Grid item md={6} mb={1} sm={12}>
                                <MetricHeaderText>
                                    <ExternalLink
                                        href={getDocsPath(
                                            org,
                                            docsPathConstants.personalCreditOutOfHoursDefinition
                                        )}
                                        underline="none"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Out of hours working
                                    </ExternalLink>
                                </MetricHeaderText>
                                <InfoText>
                                    Person-working days spent working outside of your normal working
                                    hours (as defined in their profile).
                                </InfoText>
                                <Stack
                                    direction="column"
                                    alignItems="left"
                                    spacing={1}
                                    mt={1}
                                    style={{ width: "100%" }}
                                >
                                    <InfoText>Data source impact:</InfoText>
                                    {calculateDependencies(
                                        data &&
                                            data.outOfHours &&
                                            data.outOfHours.meta.accuracy.dependencies
                                    )}
                                </Stack>
                            </Grid>
                            <Grid item md={6} mb={1} sm={12}>
                                <MetricHeaderText>
                                    <ExternalLink
                                        href={getDocsPath(
                                            org,
                                            docsPathConstants.personalCreditFocusDefinition
                                        )}
                                        underline="none"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Available Focus Time
                                    </ExternalLink>
                                </MetricHeaderText>
                                <InfoText>
                                    Person-working days available for deep focus time (different
                                    from code days, as it assumes you can not get into deep focus
                                    without at least 1 continuous hour uninterrupted).
                                </InfoText>
                                <Stack
                                    direction="column"
                                    alignItems="left"
                                    spacing={1}
                                    mt={1}
                                    style={{ width: "100%" }}
                                >
                                    <InfoText>Data source impact:</InfoText>
                                    {calculateDependencies(
                                        data && data.focus && data.focus.meta.accuracy.dependencies
                                    )}
                                </Stack>
                            </Grid>
                        </Grid>
                    </>
                )}
            </CustomPaper>
        </ReactCardFlip>
    );
};

export default DevCost;
